import { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { CheckCircle2, AlertTriangle, Clock, Calendar, DollarSign, Award, History } from "lucide-react";
import { Link } from "react-router-dom";
import logoiii from "../assets/logoiii.png";
import SubscriptionPlan from "./SubscriptionPlan";

const MisPlanes = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const fetchUserSubscriptions = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("token");
        console.log('Iniciando petición para obtener suscripciones...');
        // Obtener todas las suscripciones del usuario
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/userSubscriptions/${user.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          }
        );
        console.log('Respuesta de suscripciones:', {
          status: response.status,
          data: response.data,
          headers: response.headers
        });
        // Ordenar suscripciones por fecha de inicio (más reciente primero)
        const sortedSubscriptions = [...response.data].sort((a, b) => 
          new Date(b.startDate) - new Date(a.startDate)
        );
        console.log('Suscripciones ordenadas:', sortedSubscriptions);
        // Obtener detalles de los planes para cada suscripción
        console.log('Iniciando peticiones para detalles de planes...');
        const subscriptionsWithPlanDetails = await Promise.all(
          sortedSubscriptions.map(async (subscription) => {
            try {
              const planResponse = await axios.get(
                `${process.env.REACT_APP_API_URL || 'http://localhost:4005'}/api/v1/subscriptionPlans/${subscription.subscriptionPlanId}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                }
              );
              console.log(`Respuesta del plan ${subscription.subscriptionPlanId}:`, {
                status: planResponse.status,
                data: planResponse.data,
                headers: planResponse.headers
              });
              return {
                ...subscription,
                planDetails: planResponse.data
              };
            } catch (error) {
              console.error(`Error al obtener detalles del plan ${subscription.subscriptionPlanId}:`, error);
              return {
                ...subscription,
                planDetails: { name: "Plan no disponible", description: "No se pudieron cargar los detalles" }
              };
            }
          })
        );
        
        setSubscriptions(subscriptionsWithPlanDetails);
        
        // Encontrar suscripción activa
        const now = new Date();
        const active = subscriptionsWithPlanDetails.find(sub => 
          sub.status === "active" && 
          new Date(sub.startDate) <= now && 
          new Date(sub.endDate) > now
        );
        
        setActivePlan(active || null);
      } catch (error) {
        console.error("Error al obtener las suscripciones:", error);
        setError("No se pudieron cargar tus suscripciones. Por favor, intenta de nuevo más tarde.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserSubscriptions();
  }, [user, cancelSuccess]);

  // Función para manejar la cancelación de la suscripción
  const handleCancelSubscription = async () => {
    if (!activePlan?.id) return;
    
    try {
      setCancelLoading(true);
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4005';

      const response = await axios.put(
        `${apiUrl}/api/v1/userSubscriptions/user-cancel/${activePlan.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
        );

      setCancelSuccess(true);
      setShowCancelDialog(false);
      
      // Actualizar la información del plan activo para reflejar la cancelación
      setActivePlan(prev => prev ? {...prev, status: 'cancelled'} : null);
      
      // Mostrar mensaje de éxito
      alert(response.data.message || "Tu suscripción ha sido cancelada. Podrás seguir utilizando el servicio hasta el final del período contratado.");
      
    } catch (error) {
      console.error("Error al cancelar la suscripción:", error);
      alert(`Error: ${error.response?.data?.message || "No se pudo cancelar la suscripción. Intenta más tarde."}`);
    } finally {
      setCancelLoading(false);
    }
  };

  // Función para obtener el color de estado
  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-green-600/20 border-green-500/30 text-green-400";
      case "pending": return "bg-yellow-600/20 border-yellow-500/30 text-yellow-400";
      case "cancelled": return "bg-red-600/20 border-red-500/30 text-red-400";
      case "paused": return "bg-blue-600/20 border-blue-500/30 text-blue-400";
      case "rejected": return "bg-gray-600/20 border-gray-500/30 text-gray-400";
      default: return "bg-gray-600/20 border-gray-500/30 text-gray-400";
    }
  };
  
  // Función para obtener el texto de estado
  const getStatusText = (status) => {
    switch (status) {
      case "active": return "Activo";
      case "pending": return "Pendiente";
      case "cancelled": return "Cancelado";
      case "paused": return "Pausado";
      case "rejected": return "Rechazado";
      default: return "Desconocido";
    }
  };
  
  // Función para formatear fecha
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Verificar si una suscripción está activa
  const isSubscriptionActive = (subscription) => {
    const now = new Date();
    return (
      subscription.status === "active" && 
      new Date(subscription.startDate) <= now && 
      new Date(subscription.endDate) > now
    );
  };

  // Estado para controlar las pestañas
  const [activeTab, setActiveTab] = useState('planes');

  return (
    <div className="min-h-screen bg-gradient-to-b bg-black text-white font-montserrat pt-24">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center mb-8">
          <div className="w-12 h-12 mr-2">
            <img src={logoiii} alt="Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-center">
             Planes
          </h1>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        ) : error ? (
          <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-4 flex items-start max-w-3xl mx-auto">
            <AlertTriangle className="text-red-500 w-6 h-6 mr-2 flex-shrink-0 mt-1" />
            <p>{error}</p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="bg-gray-800/50 rounded-lg p-6 max-w-3xl mx-auto text-center">
            <h2 className="text-xl font-semibold mb-4">No tienes suscripciones</h2>
            <p className="text-gray-300 mb-6">Aún no has contratado ningún plan de suscripción.</p>
            <Link 
              to="/subscriptionplan" 
              className="inline-block bg-yellow-400 hover:bg-yellow-500 text-black font-medium py-2 px-6 rounded-full transition-colors"
            >
              Ver planes disponibles
            </Link>
          </div>
        ) : (
          <div className="space-y-6 max-w-4xl mx-auto">
            {/* Sistema de pestañas */}
            <div className="border-b border-gray-700 mb-6">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('planes')}
                  className={`pb-3 px-1 relative ${activeTab === 'planes' 
                    ? 'text-yellow-400 font-medium' 
                    : 'text-gray-400 hover:text-gray-200'}`}
                >
                  <div className="flex items-center">
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    <span>Mis Planes</span>
                  </div>
                  {activeTab === 'planes' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400"></div>
                  )}
                </button>
                
                <button
                  onClick={() => setActiveTab('historial')}
                  className={`pb-3 px-1 relative ${activeTab === 'historial' 
                    ? 'text-yellow-400 font-medium' 
                    : 'text-gray-400 hover:text-gray-200'}`}
                >
                  <div className="flex items-center">
                    <History className="w-5 h-5 mr-2" />
                    <span>Historial</span>
                  </div>
                  {activeTab === 'historial' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400"></div>
                  )}
                </button>
              </div>
            </div>
            
            {/* Contenido de la pestaña Mis Planes */}
            {activeTab === 'planes' && (
              <div className="space-y-8">
                {/* Sección de plan activo */}
                {activePlan && (
                  <div>
                    <h2 className="text-xl font-semibold flex items-center">
                      <CheckCircle2 className="text-green-500 w-5 h-5 mr-2" />
                      Plan Activo
                    </h2>
                    <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-600/20 border border-yellow-500/30 rounded-lg p-5">
                      <div className="flex flex-col md:flex-row justify-between gap-4">
                        <div>
                          <h3 className="text-xl font-semibold text-yellow-400">{activePlan.planDetails.name}</h3>
                          <p className="text-sm text-gray-300 mt-1">{activePlan.planDetails.description}</p>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="text-sm">
                                <span className="text-gray-400">Precio:</span>{" "}
                                <span className="text-white font-medium">${activePlan.planDetails.price}</span>
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="text-sm">
                                <span className="text-gray-400">Duración:</span>{" "}
                                <span className="text-white font-medium">{activePlan.planDetails.duration} días</span>
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="text-sm">
                                <span className="text-gray-400">Inicio:</span>{" "}
                                <span className="text-white font-medium">{formatDate(activePlan.startDate)}</span>
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="text-sm">
                                <span className="text-gray-400">Vence:</span>{" "}
                                <span className="text-white font-medium">{formatDate(activePlan.endDate)}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-center justify-center">
                          <div className={`${getStatusColor(activePlan.status)} border rounded-full px-4 py-1 text-sm font-medium mb-2`}>
                            {getStatusText(activePlan.status)}
                          </div>
                          <div className="text-xs text-gray-400">
                            ID: {typeof activePlan.id === 'string' ? activePlan.id.substring(0, 8) : activePlan.id}
                          </div>
                          
                          {/* Botón para cancelar la suscripción */}
                          {activePlan.status === "active" && (
                            <button 
                              onClick={() => setShowCancelDialog(true)} 
                              className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-1 rounded-full text-sm transition-colors"
                            >
                              Cancelar suscripción
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <h2 className="text-xl font-semibold flex items-center">
                      <CheckCircle2 className="text-green-500 w-5 h-5 mr-2" />
                      Otros Planes Disponibles
                    </h2>
                {/* Botón para ver planes disponibles */}
                <div className="flex justify-center ">
                  
                  <SubscriptionPlan/>
                </div>
              </div>
            )}
            
            {/* Contenido de la pestaña Historial */}
            {activeTab === 'historial' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  {subscriptions.map((subscription) => (
                    <div 
                      key={subscription.id}
                      className={`bg-gray-800/50 border ${isSubscriptionActive(subscription) ? 'border-green-500/30' : 'border-gray-700'} rounded-lg p-4`}
                    >
                      <div className="flex flex-col sm:flex-row justify-between gap-2">
                        <div>
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium">{subscription.planDetails.name}</h3>
                            {isSubscriptionActive(subscription) && (
                              <span className="ml-2 bg-green-600/20 border border-green-500/30 text-green-400 text-xs px-2 py-0.5 rounded-full">
                                Activo
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm">
                            <div className="flex items-center">
                              <DollarSign className="w-3.5 h-3.5 mr-1 text-gray-400" />
                              <span className="text-gray-400">Precio:</span>{" "}
                              <span className="ml-1">${subscription.planDetails.price}</span>
                            </div>
                            <div className="flex items-center">
                              <Calendar className="w-3.5 h-3.5 mr-1 text-gray-400" />
                              <span className="text-gray-400">Periodo:</span>{" "}
                              <span className="ml-1">{formatDate(subscription.startDate)} - {formatDate(subscription.endDate)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end justify-center">
                          <div className={`${getStatusColor(subscription.status)} border rounded-full px-3 py-0.5 text-xs font-medium`}>
                            {getStatusText(subscription.status)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Modal de confirmación para cancelar la suscripción */}
      {showCancelDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-70">
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">
              Cancelar suscripción
            </h3>
            <p className="text-gray-300 mb-6">
              ¿Estás seguro que deseas cancelar tu suscripción? Podrás seguir utilizando el servicio hasta el final del período contratado.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowCancelDialog(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                disabled={cancelLoading}
              >
                No, mantener
              </button>
              <button
                onClick={handleCancelSubscription}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                disabled={cancelLoading}
              >
                {cancelLoading ? "Cancelando..." : "Sí, cancelar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MisPlanes;
